import React, { FC, useEffect, useState, Suspense } from 'react';
import { EventBus } from '@vtblife/event-bus';
import { OpenChatEventType, OpenChatPayload, CloseChatEventType, CloseChatPayload } from '@vtblife/event-bus-events';
import { MicroBundle } from '@vtblife/micro-frontend-core';
import { Spinner } from '@vtblife/uikit';

import { getApplicationPath } from '../../libs/micro-bundle-utils';
import { useChatUrlParam } from '../../hooks/useChatUrlParam';
import { useGlobalContext, useOnChangeGlobalContext } from '../GlobalContext';

const CHAT_APP_NAME = 'chat';
const CDN_URL = 'https://cdn.m2.ru';

export const ChatApp: FC = () => {
    const [ chatFromUrl, { setUrlParam } ] = useChatUrlParam();
    const [ isOpen, setIsOpen ] = useState(chatFromUrl);
    const [ chatData, setChatData ] = useState<OpenChatPayload | null>(null);

    const globalContext = useGlobalContext();
    const { router } = useOnChangeGlobalContext();
    const { auth, env } = globalContext;

    const handleClose = React.useCallback(() => {
        setIsOpen(false);
        setUrlParam(false);
        setChatData(null);
    }, [ setUrlParam ]);

    // Для local и testing окружения используем 'test'
    const appEnvironment = env === 'local' || env === 'testing' ? 'test' : env;

    // Создаём history adapter, используя router из контекста. Для корректной типизации с MicroBundle.
    const historyAdapter = React.useMemo(() => ({
        location: router.location,
        listen: () => {
            return () => {};
        },
        push: (path: string) => {
            router.update(path);
        },
        replace: (path: string) => {
            router.update(path, { replace: true });
        },
        go: (n: number) => {
            if (typeof window !== 'undefined') {
                window.history.go(n);
            }
        },
        goBack: () => {
            if (typeof window !== 'undefined') {
                window.history.back();
            }
        },
        goForward: () => {
            if (typeof window !== 'undefined') {
                window.history.forward();
            }
        }
    }), [ router ]);

    useEffect(() => {
        const searchParams = new URLSearchParams(router.location.search);
        const shouldBeOpen = searchParams.get('chat') === 'true';

        if (shouldBeOpen && ! isOpen) {
            setIsOpen(true);
        } else if (! shouldBeOpen && isOpen) {
            setIsOpen(false);
        }
    }, [ router.location.search, isOpen ]);

    const eventBus = EventBus.getInstance();

    useEffect(() => {
        const subscriptions = [
            eventBus.subscribe<OpenChatEventType, OpenChatPayload>('chat:open', ({ data }) => {
                setChatData(data);
                setIsOpen(true);
                setUrlParam(true);
            }),
            eventBus.subscribe<CloseChatEventType, CloseChatPayload>('chat:close', () => {
                setIsOpen(false);
                setUrlParam(false);
                setChatData(null);
            })
        ];

        return () => {
            subscriptions.forEach(subscription => subscription.unsubscribe());
        };
    }, [ setUrlParam, eventBus ]);

    // Пере-публикуем событие с данными, когда чат становится открытым
    useEffect(() => {
        if (isOpen && chatData && eventBus) {
            // Небольшая задержка, чтобы микрофронтенд успел загрузиться
            const timer = setTimeout(() => {
                eventBus.publish({
                    type: 'chat:open',
                    category: 'simple',
                    data: chatData
                });
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [ isOpen, chatData, eventBus ]);

    const chatPath = getApplicationPath({
        appEnvironment,
        applicationName: CHAT_APP_NAME,
        cdnUrl: CDN_URL,
    });

    // @ts-expect-error - auth может иметь поле isAuth в зависимости от источника
    const isUserAuthenticated = Boolean(auth?.isAuth || auth?.isAuthenticated);

    return (
        <div style={{ display: isOpen ? 'block' : 'none' }}>
            <Suspense fallback={<Spinner />}>
                <MicroBundle
                    name={CHAT_APP_NAME}
                    path={chatPath}
                    history={historyAdapter}
                    authInfo={{
                        isAuthenticated: isUserAuthenticated,
                        user: auth,
                    }}
                    embed={false}
                    withOwnSidebar
                    onClose={handleClose}
                />
            </Suspense>
        </div>
    );
};

